import { useState } from 'react'

import { createChatroomMessage, createChatroomWithMessage } from '../../../api'
import { EMPTY_LIST } from '../../../constants/common'
import { useInlineNotifications, useNotification } from '../../../hooks'
import { FormikForm } from '../../Formik'
import {
  CHATROOM_INPUT_FIELD_NAME_MESSAGE,
  CHATROOM_INPUT_INITIAL_VALUES,
} from '../Chatroom.constants'
import { saveChatroomComposeDraft } from '../Chatroom.utils'

import {
  getChatroomCreateMessagePayload,
  getChatroomCreateWithMessagePayload,
  getChatroomInputDraftKey,
} from './ChatroomInputForm.utils'
import { ChatroomInputFormFields } from './ChatroomInputFormFields'

function ChatroomInputForm({
  buildingId,
  canSendAiGeneratedMessage,
  canSendTemplatedMessage,
  chatroomUuid,
  createChatroomParams,
  isFullScreen,
  isPrivate,
  isPublic,
  messageDraftRef,
  outboundUuid,
  requestId,
  onChatroomCreated,
  onSendSuccess,
}) {
  const { handleAddErrorNotification } = useNotification()

  const { handleAddNotification, handleRemoveNotification, notifications } =
    useInlineNotifications()

  const [files, setFiles] = useState(EMPTY_LIST)

  const [storageFiles, setStorageFiles] = useState(EMPTY_LIST)

  const [modal, setModal] = useState(null)

  const [isSubmitting, setSubmitting] = useState(false)

  const draftKey = getChatroomInputDraftKey({ chatroomUuid, isPublic })

  function handleCloseModal() {
    setModal(null)
  }

  function resetAfterSend(resetForm) {
    saveChatroomComposeDraft(messageDraftRef, null)
    resetForm({ values: CHATROOM_INPUT_INITIAL_VALUES })
    setFiles(EMPTY_LIST)
    setStorageFiles(EMPTY_LIST)
  }

  function handleSendMessage(text, resetForm) {
    setSubmitting(true)

    createChatroomMessage({
      data: getChatroomCreateMessagePayload({
        chatroom: chatroomUuid,
        files: [...files, ...storageFiles],
        text,
      }),
      outboundUuid,
    })
      .then(response => {
        if (response) {
          resetAfterSend(resetForm)
          onSendSuccess?.(response.data)
        }
      })
      .catch(() => {
        handleAddErrorNotification('Error.CommonApiRequestFailKey')
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  function handleCreateChatroom(text, resetForm) {
    setSubmitting(true)

    createChatroomWithMessage({
      data: getChatroomCreateWithMessagePayload({
        createChatroomParams,
        files: [...files, ...storageFiles],
        isPublic,
        text,
      }),
      outboundUuid,
    })
      .then(response => {
        if (response) {
          resetAfterSend(resetForm)
          onChatroomCreated?.(response.data)
        }
      })
      .catch(() => {
        handleAddErrorNotification('Error.CommonApiRequestFailKey')
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  function handleSubmit(values, { resetForm }) {
    const text = values[CHATROOM_INPUT_FIELD_NAME_MESSAGE]

    if (chatroomUuid) {
      handleSendMessage(text, resetForm)
    } else {
      handleCreateChatroom(text, resetForm)
    }
  }

  return (
    <FormikForm
      initialValues={CHATROOM_INPUT_INITIAL_VALUES}
      validateOnBlur={false}
      validateOnChange={false}
      onSubmit={handleSubmit}
    >
      <ChatroomInputFormFields
        buildingId={buildingId}
        canSendAiGeneratedMessage={canSendAiGeneratedMessage}
        canSendTemplatedMessage={canSendTemplatedMessage}
        draftKey={draftKey}
        files={files}
        isFullScreen={isFullScreen}
        isPrivate={isPrivate}
        isSubmitting={isSubmitting}
        messageDraftRef={messageDraftRef}
        notifications={notifications}
        outboundUuid={outboundUuid}
        requestId={requestId}
        storageFiles={storageFiles}
        onCloseModal={handleCloseModal}
        onRemoveNotification={handleRemoveNotification}
        onSetFiles={setFiles}
        onSetModal={setModal}
        onSetNotification={handleAddNotification}
        onSetStorageFiles={setStorageFiles}
      />
      {modal}
    </FormikForm>
  )
}

export default ChatroomInputForm
